import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { toast } from 'react-hot-toast';
import { useFirestore } from '../hooks/useFirestore';
import { useConfirmation } from '../hooks/useConfirmation';
import AgencyCard from '../components/Agencies/AgencyCard';
import AgencyModal from '../components/Agencies/AgencyModal';
import ConfirmationModal from '../components/Common/ConfirmationModal';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiPlus, FiSearch, FiTrash2, FiFilter, FiFileText, FiClock } = FiIcons;

const Agencies = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingAgency, setEditingAgency] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('all'); // all, prepaid, postpaid

  const { documents: agencies, loading, addDocument, updateDocument, deleteDocument } = useFirestore('agencies');
  const { documents: articles } = useFirestore('articles');
  const { confirmationState, showConfirmation, closeConfirmation } = useConfirmation();

  const handleAddAgency = () => {
    setEditingAgency(null);
    setIsModalOpen(true);
  };

  const handleEditAgency = (agency) => {
    setEditingAgency(agency);
    setIsModalOpen(true);
  };

  const handleSaveAgency = async (agencyData) => {
    try {
      if (editingAgency) {
        await updateDocument(editingAgency.id, agencyData);
        toast.success('Agency updated successfully!');
      } else {
        await addDocument(agencyData);
        toast.success('Agency created successfully!');
      }
      setIsModalOpen(false);
      setEditingAgency(null);
    } catch (error) {
      toast.error('Error saving agency: ' + error.message);
    }
  };

  const handleDeleteAgency = async (agency) => {
    const agencyArticles = articles.filter(article => article.agency_id === agency.id);
    const message = agencyArticles.length > 0 
      ? `This agency has ${agencyArticles.length} articles. Are you sure you want to delete "${agency.name}"? This will also remove the agency reference from all articles and cannot be undone.`
      : `Are you sure you want to delete "${agency.name}"? This action cannot be undone.`;

    const confirmed = await showConfirmation({
      title: 'Delete Agency',
      message,
      confirmText: 'Delete Agency',
      cancelText: 'Keep Agency',
      type: 'danger',
      icon: FiTrash2,
      onConfirm: async () => {
        await deleteDocument(agency.id);
        toast.success('Agency deleted successfully!');
      }
    });

    if (!confirmed) {
      toast.error('Failed to delete agency');
    }
  };

  const getAgencyArticleStats = (agencyId) => {
    const agencyArticles = articles.filter(article => article.agency_id === agencyId);
    return {
      total: agencyArticles.length,
      published: agencyArticles.filter(article => article.status === 'published').length,
      completed: agencyArticles.filter(article => article.status === 'completed').length,
      inProgress: agencyArticles.filter(article => article.status === 'in_progress').length,
      draft: agencyArticles.filter(article => article.status === 'draft').length
    };
  };

  const filteredAgencies = agencies.filter(agency => {
    const matchesSearch = agency.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      agency.contact_person.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesType = filterType === 'all' || agency.payment_type === filterType;
    
    return matchesSearch && matchesType;
  });

  // Calculate stats
  const prepaidAgencies = agencies.filter(agency => agency.payment_type === 'prepaid');
  const postpaidAgencies = agencies.filter(agency => agency.payment_type === 'postpaid');
  const totalRevenue = agencies.reduce((sum, agency) => {
    if (agency.payment_type === 'prepaid') {
      return sum + (agency.total_price || 0);
    } else {
      const stats = getAgencyArticleStats(agency.id);
      return sum + (stats.published * agency.price_per_article);
    }
  }, 0);

  const pendingBilling = postpaidAgencies.reduce((sum, agency) => {
    if (agency.billing_status === 'pending') {
      const stats = getAgencyArticleStats(agency.id);
      return sum + (stats.published * agency.price_per_article);
    }
    return sum;
  }, 0);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="loading-spinner mx-auto mb-4"></div>
          <p className="text-gray-600">Loading agencies...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="flex flex-col sm:flex-row sm:items-center sm:justify-between"
      >
        <div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Client Agencies</h1>
          <p className="text-gray-600">Manage your SEO agency clients with flexible payment options</p>
        </div>
        <button
          onClick={handleAddAgency}
          className="mt-4 sm:mt-0 bg-primary-600 text-white px-4 py-2 rounded-lg hover:bg-primary-700 transition-colors flex items-center space-x-2"
        >
          <SafeIcon icon={FiPlus} className="w-5 h-5" />
          <span>Add Client</span>
        </button>
      </motion.div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-white rounded-xl shadow-sm border border-gray-200 p-6"
        >
          <h3 className="text-lg font-semibold text-gray-900 mb-2">Total Agencies</h3>
          <p className="text-3xl font-bold text-primary-600">{agencies.length}</p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-white rounded-xl shadow-sm border border-gray-200 p-6"
        >
          <h3 className="text-lg font-semibold text-gray-900 mb-2">Prepaid Clients</h3>
          <p className="text-3xl font-bold text-blue-600">{prepaidAgencies.length}</p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="bg-white rounded-xl shadow-sm border border-gray-200 p-6"
        >
          <h3 className="text-lg font-semibold text-gray-900 mb-2">Post-Payment</h3>
          <p className="text-3xl font-bold text-purple-600">{postpaidAgencies.length}</p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="bg-white rounded-xl shadow-sm border border-gray-200 p-6"
        >
          <h3 className="text-lg font-semibold text-gray-900 mb-2">Pending Bills</h3>
          <p className="text-3xl font-bold text-warning-600">{pendingBilling.toFixed(2)} LEI</p>
        </motion.div>
      </div>

      {/* Search and Filter */}
      <div className="flex flex-col sm:flex-row gap-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="relative flex-1"
        >
          <SafeIcon icon={FiSearch} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
          <input
            type="text"
            placeholder="Search agencies..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
          />
        </motion.div>

        <div className="relative">
          <SafeIcon icon={FiFilter} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
          <select
            value={filterType}
            onChange={(e) => setFilterType(e.target.value)}
            className="pl-10 pr-8 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent appearance-none bg-white"
          >
            <option value="all">All Payment Types</option>
            <option value="prepaid">Prepaid Only</option>
            <option value="postpaid">Post-Payment Only</option>
          </select>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredAgencies.map((agency, index) => (
          <motion.div
            key={agency.id}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.1 }}
          >
            <AgencyCard
              agency={agency}
              onEdit={handleEditAgency}
              onDelete={() => handleDeleteAgency(agency)}
              articleStats={getAgencyArticleStats(agency.id)}
            />
          </motion.div>
        ))}
      </div>

      {filteredAgencies.length === 0 && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="text-center py-12"
        >
          <p className="text-gray-500 text-lg">No agencies found</p>
          <p className="text-gray-400 mt-2">
            {searchTerm || filterType !== 'all'
              ? 'Try adjusting your search or filter criteria'
              : 'Add your first client agency to get started'}
          </p>
        </motion.div>
      )}

      <AgencyModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onSave={handleSaveAgency}
        agency={editingAgency}
      />

      <ConfirmationModal
        isOpen={confirmationState.isOpen}
        onClose={closeConfirmation}
        onConfirm={confirmationState.onConfirm}
        title={confirmationState.title}
        message={confirmationState.message}
        confirmText={confirmationState.confirmText}
        cancelText={confirmationState.cancelText}
        type={confirmationState.type}
        icon={confirmationState.icon}
        isLoading={confirmationState.isLoading}
      />
    </div>
  );
};

export default Agencies;